import { useState, useRef } from "react";
import {
  Box,
  Typography,
  Button,
  Paper,
  ListItem,
  Card,
  CardContent,
  Chip,
  Tooltip,
  IconButton,
  Divider,
  Stack,
  TextField,
  useTheme,
} from "@mui/material";
import { useMedia } from "../context/MediaContext";
import AddIcon from "@mui/icons-material/Add";
import HubRounded from "@mui/icons-material/HubRounded";
import CloudOffRounded from "@mui/icons-material/CloudOffRounded";
import DeleteOutlineRounded from "@mui/icons-material/DeleteOutlineRounded";
import EditOutlined from "@mui/icons-material/EditOutlined";
import SouthWestRounded from "@mui/icons-material/SouthWestRounded";
import NorthEastRounded from "@mui/icons-material/NorthEastRounded";
import PaymentsOutlined from "@mui/icons-material/PaymentsOutlined";
import { CustomDialogHandle } from "../components/CustomDialog";
import {
  CustomDialog,
  AddingRateBox,
  EmptyState,
  Loading,
  Error,
  PageHeader,
} from "../components";
import { TProviderRate } from "../utils/types";
import { useProviders } from "../hooks/useProviders";
import { useMethods } from "../hooks/useMethods";
import { TForm } from "../utils/types";
import { translate } from "../utils/currency";
import { deleteProvRate, updateProviderTerminal } from "../utils/api";
const Providers: React.FC = () => {
  const { providers, rates, loading, error, fetchDataP } = useProviders();
  const { currency, methods } = useMethods();
  const [id, setId] = useState<string>("");
  const dialogRef = useRef<CustomDialogHandle>(null);
  const { isTablet } = useMedia();
  const theme = useTheme();
  const [dialogType, setDialogType] = useState<string>("");
  const [editTerminalId, setEditTerminalId] = useState<number | null>(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);
  const [form, setForm] = useState<TForm>({
    operation_type: null,
    currency: null,
    rate: null,
    payment_method: null,
    minsum: null,
    maxsum: null,
  });
  if (loading) {
    return <Loading variant="cards" />;
  }
  if (error) {
    return <Error error={error} />;
  }
  return (
    <>
      <Box
        sx={{
          p: { xs: 2, sm: 3 },
          maxHeight: {
            xs: "calc(var(--content-max-height) + 22dvh)",
            md: "90vh",
          },
          overflowY: "auto",
          "&::-webkit-scrollbar": { display: "none" },
          overflowX: "hidden",

          overflow: !isTablet ? "none" : "auto",
        }}
      >
        <PageHeader
          title="Провайдеры"
          description="Ставки, лимиты и доступные платёжные направления"
          icon={HubRounded}
        />
        {providers.length === 0 ? (
          <EmptyState
            icon={CloudOffRounded}
            title="Провайдеров пока нет"
            description="После подключения провайдера его ставки появятся здесь."
          />
        ) : (
        <Box
          sx={{
            overflow: isTablet ? "none" : "auto",
            "&::-webkit-scrollbar": { display: "none" },
          }}
          display="flex"
          flexDirection="column"
          gap="20px"
        >
          {providers.map((el: any) => (
            <Box
              onClick={() => {
                setId(el.id);
              }}
              key={el.id}
            >
              <ListItem
                sx={{
                  minHeight: "58px",
                  background:
                    theme.palette.mode === "dark"
                      ? "linear-gradient(135deg, #1b263d, #172036)"
                      : "linear-gradient(135deg, #f4f2ff, #eef1ff)",
                  border: `1px solid ${theme.palette.divider}`,
                  borderBottom: 0,
                  borderRadius: "16px 16px 0 0",
                  px: 2.5,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography
                  sx={{
                    
                    textTransform: "uppercase",
                  }}
                  color="text.primary"
                  variant="h5"
                >
                  {el.name}
                </Typography>
                <Chip
                  label={`${rates[el.id]?.length ?? 0} ставок`}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
              </ListItem>
              <Paper
                elevation={0}
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    sm: "repeat(auto-fill, minmax(250px, 1fr))",
                  },
                  gap: 2,
                  p: { xs: 2, sm: 2.5 },
                  borderRadius: "0 0 16px 16px",
                }}
              >
                {rates[el.id]?.map((terminal: TProviderRate, index: number) => {
                  const isIncoming = ["deposit", "cashin"].includes(
                    terminal.operation_type,
                  );
                  const OperationIcon = isIncoming
                    ? SouthWestRounded
                    : NorthEastRounded;
                  const operationLabel: Record<string, string> = {
                    deposit: "Приём",
                    cashin: "Пополнение",
                    payout: "Выплата",
                    withdrawal: "Вывод",
                  };

                  return (
                  <Card
                    key={`${terminal.operation_type}-${terminal.payment_method}-${terminal.currency}-${index}`}
                    sx={{
                      borderRadius: "18px",
                      width: "100%",
                      minHeight: 248,
                      boxShadow: "none",
                      position: "relative",
                      overflow: "hidden",
                      transition:
                        "transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease",
                      "&:hover": {
                        transform: "translateY(-3px)",
                        boxShadow:
                          theme.palette.mode === "dark"
                            ? "0 16px 30px rgba(0,0,0,.22)"
                            : "0 16px 30px rgba(31,42,68,.12)",
                        borderColor: "primary.light",
                      },
                    }}
                  >
                    <CardContent sx={{ p: 0, "&:last-child": { pb: 0 } }}>
                      <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        spacing={1}
                        sx={{ p: 2, pb: 1.75 }}
                      >
                        <Stack
                          direction="row"
                          alignItems="center"
                          spacing={1.25}
                          minWidth={0}
                        >
                          <Box
                            sx={{
                              width: 40,
                              height: 40,
                              display: "grid",
                              placeItems: "center",
                              flexShrink: 0,
                              borderRadius: "12px",
                              color: isIncoming ? "success.main" : "info.main",
                              bgcolor: isIncoming
                                ? "rgba(23,166,115,.10)"
                                : "rgba(62,139,243,.10)",
                            }}
                          >
                            <PaymentsOutlined fontSize="small" />
                          </Box>
                          <Box minWidth={0}>
                            <Tooltip
                              title={`Метод: ${terminal.payment_method}`}
                              arrow
                            >
                              <Typography
                                variant="subtitle1"
                                fontWeight={700}
                                noWrap
                              >
                                {translate(terminal.payment_method)}
                              </Typography>
                            </Tooltip>
                            <Chip
                              icon={<OperationIcon />}
                              label={
                                operationLabel[terminal.operation_type] ??
                                terminal.operation_type
                              }
                              size="small"
                              color={isIncoming ? "success" : "info"}
                              variant="outlined"
                              sx={{ mt: 0.5, height: 24 }}
                            />
                          </Box>
                        </Stack>
                        <Stack direction="row" spacing={0.5} flexShrink={0}>
                          <Tooltip title="Изменить ставку">
                            <IconButton
                              aria-label="Изменить ставку"
                              size="small"
                              onClick={(event) => {
                                event.stopPropagation();
                                setId(el.id);
                                setEditTerminalId(
                                  terminal.id ??
                                    terminal.provider_terminal_id ??
                                    null,
                                );
                                setDialogType("edit");
                                dialogRef.current?.openDialog();
                                setForm({
                                  operation_type: terminal.operation_type,
                                  currency: terminal.currency,
                                  rate: terminal.rate,
                                  payment_method: terminal.payment_method,
                                  minsum: terminal.minsum,
                                  maxsum: terminal.maxsum,
                                });
                              }}
                            >
                              <EditOutlined fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Удалить ставку">
                            <IconButton
                              aria-label="Удалить ставку"
                              color="error"
                              size="small"
                              onClick={(event) => {
                                event.stopPropagation();
                                setId(el.id);
                                setDialogType("delete");
                                dialogRef.current?.openDialog();
                                setForm({
                                  operation_type: terminal.operation_type,
                                  currency: terminal.currency,
                                  rate: terminal.rate,
                                  payment_method: terminal.payment_method,
                                  minsum: terminal.minsum,
                                  maxsum: terminal.maxsum,
                                });
                              }}
                            >
                              <DeleteOutlineRounded fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </Stack>

                      <Divider />

                      <Box sx={{ p: 2 }}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{
                            display: "block",
                            mb: 1,
                            fontWeight: 700,
                            letterSpacing: ".06em",
                            textTransform: "uppercase",
                          }}
                        >
                          Лимиты операции
                        </Typography>
                        <Box
                          sx={{
                            display: "grid",
                            gridTemplateColumns: "1fr 1fr",
                            gap: 1,
                          }}
                        >
                          <Box
                            sx={{
                              p: 1.25,
                              borderRadius: "11px",
                              bgcolor: "action.hover",
                            }}
                          >
                            <Typography variant="caption" color="text.secondary">
                              Минимум
                            </Typography>
                            <Typography fontWeight={750}>
                              {Number(terminal.minsum).toLocaleString("ru-RU")}
                            </Typography>
                          </Box>
                          <Box
                            sx={{
                              p: 1.25,
                              borderRadius: "11px",
                              bgcolor: "action.hover",
                            }}
                          >
                            <Typography variant="caption" color="text.secondary">
                              Максимум
                            </Typography>
                            <Typography fontWeight={750}>
                              {Number(terminal.maxsum).toLocaleString("ru-RU")}
                            </Typography>
                          </Box>
                        </Box>

                        <Stack
                          direction="row"
                          alignItems="center"
                          justifyContent="space-between"
                          sx={{ mt: 1.5 }}
                        >
                          <Chip
                            label={terminal.currency.toUpperCase()}
                            size="small"
                            variant="outlined"
                            sx={{ fontWeight: 750 }}
                          />
                          <Stack direction="row" alignItems="baseline" spacing={0.4}>
                            <Typography variant="caption" color="text.secondary">
                              Ставка
                            </Typography>
                            <Typography
                              variant="h6"
                              color="primary.main"
                              fontWeight={800}
                            >
                              {terminal.rate}%
                            </Typography>
                          </Stack>
                        </Stack>
                      </Box>
                    </CardContent>
                  </Card>
                  );
                })}
                <Card
                  onClick={() => {
                    setDialogType("add");
                    dialogRef?.current?.openDialog();
                  }}
                  sx={{
                    minHeight: 248,
                    width: "100%",
                    borderRadius: "18px",
                    border: `2px dashed ${theme.palette.divider}`,
                    display: "grid",
                    placeItems: "center",
                    cursor: "pointer",
                    backgroundColor: "transparent",
                    boxShadow: "none",
                    transition:
                      "background-color 0.2s ease, border-color 0.2s ease, transform 0.2s ease",
                    "&:hover": {
                      backgroundColor: "rgba(99, 91, 255, 0.06)",
                      borderColor: theme.palette.primary.main,
                      transform: "translateY(-3px)",
                    },
                  }}
                >
                  <Stack alignItems="center" spacing={1} color="text.secondary">
                    <Box
                      sx={{
                        width: 46,
                        height: 46,
                        display: "grid",
                        placeItems: "center",
                        borderRadius: "14px",
                        bgcolor: "rgba(99,91,255,.10)",
                        color: "primary.main",
                      }}
                    >
                      <AddIcon />
                    </Box>
                    <Typography fontWeight={700}>Добавить ставку</Typography>
                  </Stack>
                </Card>
              </Paper>
            </Box>
          ))}
        </Box>
        )}
      </Box>{" "}
      <CustomDialog
        ref={dialogRef}
        dialog={{
          title:
            dialogType === "delete"
              ? "Подтвердите удаление метода"
              : dialogType === "edit"
              ? "Изменение ставки"
              : "Введите данные",
          context:
            dialogType === "delete"
              ? "  Вы точно хотите удалить эту карточку? Действие нельзя отменить."
              : null,

          sx: {
            display: "flex",
            flexDirection: "column",
            padding: "25px",
          },
        }}
      >
        {dialogType === "delete" ? (
          <Box gap="15px" display="flex" flexDirection="column" width="100%">
            <Button
              variant="contained"
              color="error"
              onClick={async () => {
                dialogRef?.current?.openDialog();
                await deleteProvRate(
                  Number(id),
                  form.payment_method ?? "",
                  form.operation_type ?? "",
                  form.currency?.toLowerCase() ?? "",
                  Number(form.rate),
                  Number(form.minsum),
                  Number(form.maxsum)
                );
                setForm({
                  operation_type: null,
                  currency: null,
                  rate: null,
                  payment_method: null,
                  minsum: null,
                  maxsum: null,
                });
                await fetchDataP();
              }}
            >
              Удалить
            </Button>
            <Button
              onClick={() => dialogRef?.current?.closeDialog()}
              variant="outlined"
            >
              Отмена
            </Button>
          </Box>
        ) : dialogType === "edit" ? (
          <Box gap="16px" display="flex" flexDirection="column" width="100%">
            <Typography variant="body2" color="text.secondary">
              {translate(form.payment_method ?? "")} ·{" "}
              {form.operation_type} · {String(form.currency).toUpperCase()}
            </Typography>

            <TextField
              label="Ставка, %"
              type="number"
              size="small"
              value={form.rate ?? ""}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, rate: e.target.value as any }))
              }
            />
            <TextField
              label="Минимум"
              type="number"
              size="small"
              value={form.minsum ?? ""}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, minsum: e.target.value as any }))
              }
            />
            <TextField
              label="Максимум"
              type="number"
              size="small"
              value={form.maxsum ?? ""}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, maxsum: e.target.value as any }))
              }
            />

            {editError && (
              <Typography variant="body2" color="error">
                {editError}
              </Typography>
            )}

            <Button
              variant="contained"
              disabled={savingEdit || editTerminalId === null}
              onClick={async () => {
                const rate = Number(form.rate);
                const minsum = Number(form.minsum);
                const maxsum = Number(form.maxsum);

                if (!Number.isFinite(rate) || rate < 0) {
                  setEditError("Ставка должна быть неотрицательным числом");
                  return;
                }
                if (minsum >= maxsum) {
                  setEditError("Минимум должен быть меньше максимума");
                  return;
                }

                setSavingEdit(true);
                setEditError(null);
                try {
                  await updateProviderTerminal(editTerminalId as number, {
                    rate,
                    minsum,
                    maxsum,
                  });
                  dialogRef?.current?.closeDialog();
                  await fetchDataP();
                } catch (err: any) {
                  const detail = err?.response?.data?.detail;
                  setEditError(
                    typeof detail === "string"
                      ? detail
                      : "Не удалось сохранить изменения",
                  );
                } finally {
                  setSavingEdit(false);
                }
              }}
            >
              {savingEdit ? "Сохранение…" : "Сохранить"}
            </Button>
            <Button
              onClick={() => dialogRef?.current?.closeDialog()}
              variant="outlined"
              disabled={savingEdit}
            >
              Отмена
            </Button>
          </Box>
        ) : (
          <AddingRateBox
            form={form}
            currency={currency}
            methods={methods}
            setForm={setForm}
            type={"provider"}
            dialogRef={dialogRef}
            providerId={id}
            fetchDataP={fetchDataP}
          />
        )}
      </CustomDialog>
    </>
  );
};
export default Providers;
